import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { 
  User, 
  Mail, 
  Lock, 
  UserPlus, 
  AlertCircle,
  CheckCircle,
  Sparkles,
  Shield,
  Zap,
  Globe,
  Building
} from 'lucide-react';

const SignUpPage: React.FC = () => {
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    confirmPassword: '', 
    companyName: '' 
  });
  const { user, signUp, loading } = useAuth();
  const { theme } = useTheme();
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Calculate password strength
    if (name === 'password') {
      let strength = 0;
      if (value.length >= 6) strength += 25;
      if (value.length >= 8) strength += 25;
      if (/[A-Z]/.test(value)) strength += 25;
      if (/[0-9]/.test(value)) strength += 25;
      setPasswordStrength(strength);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Validate password strength
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    try {
      await signUp(formData.email, formData.password, formData.name);
      // TODO: Create tenant with company name
      // This will be implemented when we add the tenant creation API
    } catch (err: any) {
      setError(err.message || 'Failed to create an account.');
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return 'bg-red-500';
    if (passwordStrength < 50) return 'bg-orange-500';
    if (passwordStrength < 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 25) return 'Weak';
    if (passwordStrength < 50) return 'Fair';
    if (passwordStrength < 75) return 'Good';
    return 'Strong';
  };

  return (
    <div className={`min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-emerald-900 via-teal-900 to-cyan-900' 
        : 'bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-100'
    }`}>
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute -top-40 -right-32 w-80 h-80 rounded-full blur-3xl ${
          theme === 'dark'
            ? 'bg-gradient-to-r from-emerald-600 to-teal-600 opacity-30'
            : 'bg-gradient-to-r from-emerald-400 to-teal-500 opacity-20'
        }`}></div>
        <div className={`absolute -bottom-40 -left-32 w-80 h-80 rounded-full blur-3xl ${
          theme === 'dark'
            ? 'bg-gradient-to-r from-cyan-600 to-blue-600 opacity-30'
            : 'bg-gradient-to-r from-cyan-400 to-blue-500 opacity-20'
        }`}></div>
      </div>

      <div className="relative z-10 w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        {/* Left side - Sign up form */}
        <div className="w-full max-w-md mx-auto lg:mx-0 order-2 lg:order-1">
          <Card className={`backdrop-blur-sm shadow-xl ${
            theme === 'dark'
              ? 'bg-white/10 border-white/20'
              : 'bg-white/80 border-white/20'
          }`}>
            <CardHeader className="space-y-1 text-center pb-8">
              <div className="mx-auto w-16 h-16 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
                <UserPlus className="w-8 h-8 text-white" />
              </div>
              <CardTitle className={`text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Create your account
              </CardTitle>
              <CardDescription className={`${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Start your customer success journey today
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className={`flex items-center space-x-2 p-4 rounded-lg ${
                    theme === 'dark'
                      ? 'bg-red-900/50 border border-red-800'
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-red-300' : 'text-red-700'
                    }`}>{error}</p>
                  </div>
                )}
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Full name
                    </Label>
                    <div className="relative">
                      <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                      }`} />
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        required
                        className="pl-10 h-12"
                        placeholder="Enter your full name"
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Email address
                    </Label>
                    <div className="relative">
                      <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                      }`} />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        className="pl-10 h-12"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="companyName" className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Company name
                    </Label>
                    <div className="relative">
                      <Building className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                      }`} />
                      <Input
                        id="companyName"
                        name="companyName"
                        type="text"
                        required
                        className="pl-10 h-12"
                        placeholder="Enter your company name"
                        value={formData.companyName}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password" className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Password
                    </Label>
                    <div className="relative">
                      <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                      }`} />
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        required
                        className="pl-10 h-12"
                        placeholder="Create a strong password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                    </div>
                    
                    {/* Password strength indicator */}
                    {formData.password && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className={`${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}>Password strength</span>
                          <span className={`font-medium ${
                            passwordStrength >= 75 ? 'text-green-600' : 
                            passwordStrength >= 50 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {getPasswordStrengthText()}
                          </span>
                        </div>
                        <div className={`w-full rounded-full h-2 ${
                          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                        }`}>
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                            style={{ width: `${passwordStrength}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Confirm password
                    </Label>
                    <div className="relative">
                      <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                      }`} />
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        required
                        className="pl-10 h-12"
                        placeholder="Confirm your password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 hover:shadow-md"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <LoadingSpinner size="sm" />
                      <span>Creating account...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <UserPlus className="w-5 h-5" />
                      <span>Create account</span>
                    </div>
                  )}
                </Button>
                
                <div className={`text-center pt-4 border-t ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Already have an account?{' '}
                    <Link
                      to="/login"
                      className={`font-medium transition-colors ${
                        theme === 'dark'
                          ? 'text-emerald-400 hover:text-emerald-300'
                          : 'text-emerald-600 hover:text-emerald-500'
                      }`}
                    >
                      Sign in
                    </Link>
                  </p>
                </div>

                {/* Terms */}
                <p className={`text-xs text-center leading-relaxed ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  By creating an account, you agree to our{' '}
                  <Link to="/terms" className={`transition-colors ${
                    theme === 'dark'
                      ? 'text-emerald-400 hover:text-emerald-300'
                      : 'text-emerald-600 hover:text-emerald-500'
                  }`}>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className={`transition-colors ${
                    theme === 'dark'
                      ? 'text-emerald-400 hover:text-emerald-300'
                      : 'text-emerald-600 hover:text-emerald-500'
                  }`}>
                    Privacy Policy
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Right side - Marketing content */}
        <div className="space-y-8 order-1 lg:order-2">
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className={`text-4xl font-bold bg-clip-text text-transparent ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-white to-gray-300'
                    : 'bg-gradient-to-r from-gray-900 to-gray-600'
                }`}>
                  Join Edyt Today
                </h1>
                <p className={`text-lg mt-1 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Unlock the power of AI-driven customer success
                </p>
              </div>
            </div>
            
            <p className={`text-xl leading-relaxed ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Transform your customer relationships with intelligent insights from your existing 
              communication channels. Built for growing teams who want to understand their customers better.
            </p>
          </div>

          {/* Benefits */}
          <div className="space-y-4">
            <div className={`flex items-start space-x-4 p-4 backdrop-blur-sm rounded-lg shadow-sm ${
              theme === 'dark'
                ? 'bg-white/10 border border-white/20'
                : 'bg-white/50 border border-white/20'
            }`}>
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Free to start</h3>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Get started with our generous free tier. No credit card required.
                </p>
              </div>
            </div>

            <div className={`flex items-start space-x-4 p-4 backdrop-blur-sm rounded-lg shadow-sm ${
              theme === 'dark'
                ? 'bg-white/10 border border-white/20'
                : 'bg-white/50 border border-white/20'
            }`}>
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Instant setup</h3>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Connect your Slack and start discovering customers in minutes.
                </p>
              </div>
            </div>

            <div className={`flex items-start space-x-4 p-4 backdrop-blur-sm rounded-lg shadow-sm ${
              theme === 'dark'
                ? 'bg-white/10 border border-white/20'
                : 'bg-white/50 border border-white/20'
            }`}>
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Enterprise security</h3>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  SOC 2 compliant with advanced encryption and data protection.
                </p>
              </div>
            </div>

            <div className={`flex items-start space-x-4 p-4 backdrop-blur-sm rounded-lg shadow-sm ${
              theme === 'dark'
                ? 'bg-white/10 border border-white/20'
                : 'bg-white/50 border border-white/20'
            }`}>
              <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Global scale</h3>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Multi-tenant architecture built for teams of all sizes worldwide.
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className={`backdrop-blur-sm rounded-lg p-6 shadow-sm ${
            theme === 'dark'
              ? 'bg-white/10 border border-white/20'
              : 'bg-white/50 border border-white/20'
          }`}>
            <div className="text-center space-y-4">
              <h3 className={`text-lg font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>Ready to get started?</h3>
              <p className={`${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}>
                Join our growing community of teams who are discovering valuable customer insights 
                from their communication channels. Start your free trial today - no credit card required.
              </p>
              <div className={`flex items-center justify-center space-x-4 text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span>Free to start</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                  <span>Setup in minutes</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                  <span>Cancel anytime</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;